import type {
  CustomerFulfillmentStatusV1,
  CustomerOrderNotificationHistoryV1,
} from "@lifescapsule/contracts";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../../shared/api";
import { useAuthSession } from "../../shared/AuthSession";
import {
  confirmPhysicalOrderAddress,
  getOrderNotifications,
  getOriginalOperation,
  getPhysicalOrderStatus,
  getPhysicalOrderTrackingUrl,
  requestPhysicalOrderCancellation,
  type DeliveryAddressInput,
  type OriginalOperation,
} from "./physical-order-api";

type FulfillmentState = CustomerFulfillmentStatusV1["state"];

export const attentionStates = new Set<FulfillmentState>([
  "RECONCILIATION_REQUIRED",
  "ON_HOLD",
  "NEEDS_ATTENTION",
  "CANCEL_REQUESTED",
  "CANCELLED",
  "RETURNED",
]);

/** Why the usual journey has stopped, in the buyer's terms. */
export const pausedCopy: Partial<Record<FulfillmentState, { title: string; detail: string }>> = {
  RECONCILIATION_REQUIRED: {
    title: "We’re confirming your order with our printer",
    detail: "This usually takes a few minutes. The steps carry on as soon as we hear back.",
  },
  CANCEL_REQUESTED: {
    title: "Your cancellation request is being checked",
    detail: "We’ll confirm here whether printing could be stopped.",
  },
  CANCELLED: {
    title: "This order was cancelled",
    detail: "Nothing more will be printed or shipped for it.",
  },
  RETURNED: {
    title: "Your book is coming back to us",
    detail: "The carrier couldn’t deliver it. Contact support and we’ll arrange the next step.",
  },
};
export const pausedDefault = {
  title: "This order needs a closer look",
  detail: "You don’t need to do anything. This page shows the latest, and support can help.",
};

export function sampleHeadline(state: FulfillmentState) {
  if (["DELIVERED", "RETURNED"].includes(state)) return "Sample order journey complete";
  if (["SHIPPED", "IN_TRANSIT", "PARTIALLY_SHIPPED"].includes(state))
    return "Sample order delivery step";
  return "Sample order prepared";
}

export function isSimulation(status: CustomerFulfillmentStatusV1) {
  return status.simulation || (!status.physicalShipment && !status.printerTest);
}

export function arrivalWindow(estimate: CustomerFulfillmentStatusV1["safeEstimate"]) {
  if (!estimate) return undefined;
  const earliest = new Date(`${estimate.earliest}T12:00:00Z`);
  const latest = new Date(`${estimate.latest}T12:00:00Z`);
  if (
    Number.isNaN(earliest.getTime()) ||
    Number.isNaN(latest.getTime()) ||
    earliest.getTime() > latest.getTime()
  )
    return undefined;
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return {
    ...estimate,
    earliestLabel: formatter.format(earliest),
    latestLabel: formatter.format(latest),
  };
}

/**
 * One idempotency key per account, order and kind of request, kept for the tab so a retry after
 * the order page remounts is still the original request. A request with a different payload (a
 * different address) gets a new key and replaces the old one: going back to an earlier address
 * is a new request, never a replay of the earlier answer (review 2026-10-28).
 */
const requestKeys = new Map<string, { payload: string; key: string }>();
function requestKey(scope: string, payload = "") {
  const known = requestKeys.get(scope);
  if (known && known.payload === payload) return known.key;
  const key = crypto.randomUUID();
  requestKeys.set(scope, { payload, key });
  return key;
}

/**
 * Whose session this is, read from the token's own claims: it only keeps one account's order out
 * of another's page here. It is never authorization; the service checks every request.
 */
export function sessionSubject(idToken: string) {
  try {
    const payload = idToken.split(".")[1] ?? "";
    const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
    const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as {
      sub?: unknown;
    };
    return typeof claims.sub === "string" ? claims.sub : idToken;
  } catch {
    return idToken;
  }
}

/**
 * What became of a request whose answer was lost, from the service's own journal of original
 * requests, never from a guess: COMPLETED and REJECTED are the outcome of this exact request (the
 * key it was sent with); UNKNOWN means the service is still settling this one, or another request
 * for the same order, so nothing may be sent again; NOT_OBSERVED means this request isn't in the
 * journal, and only the order's own status can say whether it may be sent again.
 */
export async function originalRequestOutcome(
  idToken: string,
  physicalOrderId: string,
  operation: OriginalOperation["operation"],
  requestKey: string,
): Promise<"COMPLETED" | "REJECTED" | "UNKNOWN" | "NOT_OBSERVED"> {
  const original = await getOriginalOperation(idToken, physicalOrderId, operation);
  if (!original) return "NOT_OBSERVED";
  if (original.state === "UNKNOWN") return "UNKNOWN";
  if (original.originalRequestKey !== requestKey) return "NOT_OBSERVED";
  return original.state === "COMPLETED" ? "COMPLETED" : "REJECTED";
}

/** The order's email history as the page may show it. */
export type OrderNotificationsView =
  | { state: "loading" | "error" | "unavailable" }
  | {
      state: "ready";
      history: CustomerOrderNotificationHistoryV1;
      moreState?: "loading" | "error" | "idle";
      pagingRestarted?: boolean;
    };

type OrderView = {
  context: string;
  status?: CustomerFulfillmentStatusV1;
  state: "loading" | "ready" | "unavailable";
};

/**
 * One order's status and what the customer can do about it, for either presentation: the latest
 * answer only (an older one never replaces it), tracking per package, and requests that change
 * the order. Everything it holds belongs to one account and one order: an answer that arrives
 * after either has changed is dropped, so it can't replace the next order, reload the last one or
 * open its tracking. Each request keeps one idempotency key across retries, scoped to the account,
 * the order and what is asked; a requested change shows only what the service then reports.
 */
export function usePhysicalOrder(options: { notifications?: boolean } = {}) {
  const withNotifications = options.notifications === true;
  const auth = useAuthSession();
  const idToken = auth.tokens?.idToken;
  const { physicalOrderId = "" } = useParams<{ physicalOrderId: string }>();
  const context = idToken ? `${sessionSubject(idToken)}\u0000${physicalOrderId}` : "";
  // The account and order now on the page; continuations compare against it before acting.
  const live = useRef(context);
  useLayoutEffect(() => {
    live.current = context;
    // Leaving the order (or the page) ends it: a late answer for it acts on nothing.
    return () => {
      live.current = "";
    };
  }, [context]);
  const [view, setView] = useState<OrderView>({ context, state: "loading" });
  const [tracking, setTracking] = useState<{ context: string; shipmentId: string }>();
  const [trackingFailure, setTrackingFailure] = useState<{
    context: string;
    shipmentId: string;
    message: string;
  }>();
  const statusRequestGeneration = useRef(0);
  const [notes, setNotes] = useState<{ context: string; view: OrderNotificationsView }>();
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const notesRequestGeneration = useRef(0);

  /** Reads the status; a quiet read keeps what is shown until the new answer arrives. */
  const load = useCallback(
    async (quiet = false) => {
      if (!idToken || live.current !== context) return false;
      const requestGeneration = statusRequestGeneration.current + 1;
      statusRequestGeneration.current = requestGeneration;
      const current = () =>
        statusRequestGeneration.current === requestGeneration && live.current === context;
      if (!quiet)
        setView((shown) =>
          shown.context === context
            ? { ...shown, state: "loading" }
            : { context, state: "loading" },
        );
      try {
        const result = await getPhysicalOrderStatus(idToken, physicalOrderId);
        if (!current()) return false;
        if (result.physicalOrderId !== physicalOrderId)
          throw new Error("The status belongs to another order.");
        setView({ context, status: result, state: "ready" });
        return true;
      } catch {
        if (!current()) return false;
        if (quiet) return false;
        setView({ context, state: "unavailable" });
        return false;
      }
    },
    [idToken, physicalOrderId, context],
  );

  useEffect(() => {
    void load();
    return () => {
      statusRequestGeneration.current += 1;
    };
  }, [load]);

  /**
   * Reads the order's email history (only for a page that asks for it), fenced like the status: an
   * answer for an order or account the page has left is dropped. A quiet read keeps what is shown
   * if it fails.
   */
  const loadNotifications = useCallback(
    async (quiet = false, append = false) => {
      if (!withNotifications || !idToken || live.current !== context) return false;
      const saved = notesRef.current;
      const previous =
        saved?.context === context && saved.view.state === "ready" ? saved.view : undefined;
      const cursor = append ? previous?.history.nextCursor : undefined;
      if (append && (!cursor || previous?.moreState === "loading")) return false;
      const requestGeneration = notesRequestGeneration.current + 1;
      notesRequestGeneration.current = requestGeneration;
      const current = () =>
        notesRequestGeneration.current === requestGeneration && live.current === context;
      if (append && previous) setNotes({ context, view: { ...previous, moreState: "loading" } });
      else if (!quiet) setNotes({ context, view: { state: "loading" } });
      try {
        const history = await getOrderNotifications(idToken, physicalOrderId, cursor ?? undefined);
        if (!current()) return false;
        if (history?.physicalOrderId !== physicalOrderId)
          throw new Error("The email history belongs to another order.");
        if (cursor && history.nextCursor === cursor) throw new Error("Repeated email continuation");
        setNotes({
          context,
          view: {
            state: "ready",
            pagingRestarted: quiet && Boolean(previous),
            history:
              (append || quiet) && previous
                ? {
                    ...history,
                    notifications: [
                      ...previous.history.notifications.filter(
                        (known) =>
                          !known.recordId ||
                          !history.notifications.some((n) => n.recordId === known.recordId),
                      ),
                      ...history.notifications,
                    ],
                  }
                : history,
          },
        });
        return true;
      } catch (cause) {
        if (!current()) return false;
        const accessLost = cause instanceof ApiError && [401, 403, 404, 410].includes(cause.status);
        setNotes((shown) =>
          !accessLost &&
          (quiet || append) &&
          shown?.context === context &&
          shown.view.state === "ready"
            ? { ...shown, view: { ...shown.view, moreState: "error" } }
            : {
                context,
                view: {
                  state: accessLost ? "unavailable" : "error",
                },
              },
        );
        return false;
      }
    },
    [withNotifications, idToken, physicalOrderId, context],
  );

  useEffect(() => {
    void loadNotifications();
    return () => {
      notesRequestGeneration.current += 1;
    };
  }, [loadNotifications]);

  async function openTracking(shipmentId: string) {
    if (!idToken || live.current !== context) return;
    const opened = context;
    setTracking({ context: opened, shipmentId });
    setTrackingFailure(undefined);
    try {
      const url = await getPhysicalOrderTrackingUrl(idToken, physicalOrderId, shipmentId);
      if (live.current === opened) window.location.assign(url);
    } catch {
      if (live.current === opened)
        setTrackingFailure({
          context: opened,
          shipmentId,
          message: "Tracking is temporarily unavailable. Please try again shortly.",
        });
    } finally {
      setTracking((shown) => (shown?.context === opened ? undefined : shown));
    }
  }

  /** Asks for cancellation, then shows the service's own answer; the same key on any retry. */
  async function requestCancellation() {
    if (!idToken || live.current !== context) return;
    const asked = context;
    await requestPhysicalOrderCancellation(
      idToken,
      physicalOrderId,
      requestKey(`cancel\u0000${asked}`),
    );
    if (live.current === asked) await load(true);
  }

  /** Confirms a delivery address; the same address keeps its key on a retry. */
  async function confirmAddress(address: DeliveryAddressInput) {
    if (!idToken || live.current !== context) return;
    const asked = context;
    await confirmPhysicalOrderAddress(
      idToken,
      physicalOrderId,
      address,
      requestKey(`address\u0000${asked}`, JSON.stringify(address)),
    );
    if (live.current === asked) await load(true);
  }

  const shown = view.context === context ? view : undefined;
  return {
    idToken,
    physicalOrderId,
    status: shown?.status,
    state: shown?.state ?? "loading",
    load,
    openTracking,
    trackingShipment: tracking?.context === context ? tracking.shipmentId : undefined,
    trackingError:
      trackingFailure?.context === context
        ? { shipmentId: trackingFailure.shipmentId, message: trackingFailure.message }
        : undefined,
    requestCancellation,
    confirmAddress,
    /** Present only when the page asked for notifications. */
    notifications: withNotifications
      ? notes?.context === context
        ? notes.view
        : ({ state: "loading" } as OrderNotificationsView)
      : undefined,
    loadNotifications,
  };
}
